// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useMemo, useState } from 'react';

import { type CSSVariablesResolver, MantineProvider, type MantineThemeOverride, useMantineTheme } from '@mantine/core';
import { createPortal } from 'react-dom';
import { v4 as uuid } from 'uuid';

import { PageThemeContext } from 'app/Layout/PageColorScheme';

const stageVariables: CSSVariablesResolver = () => ({
  variables: {},
  light: {},
  // The nav's surface colour, so modals and menus on the stage read as part of the shell
  dark: { '--mantine-color-body': 'var(--mantine-color-app-shell-gray-9)' },
});

type Props = {
  className?: string;
  children: ReactNode;
};

/**
 * Mantine's dark scheme for the stage behind the page card. Unlike `WithColorScheme`, layers that portal out of it
 * (menus, combobox dropdowns, tooltips, modals, notifications) are dark too: their portal target is a dark-scheme node
 * on `body`, outside the stage's transformed and faded ancestors, so they still stack above the app shell.
 */
export const StageColorScheme = ({ className, children }: Props) => {
  const [id] = useState(() => `stage${uuid().replaceAll('-', '')}`);
  const portalId = `${id}-portal`;
  const { variantColorResolver } = useMantineTheme();
  const theme = useMemo<MantineThemeOverride>(
    () => ({
      components: { Portal: { defaultProps: { target: `#${portalId}` } } },
      // The app's `light` variant is a white button made for the light scheme; on the stage it is Mantine's own
      variantColorResolver: (input) =>
        variantColorResolver(input.variant === 'light' ? { ...input, variant: 'light-mantine' } : input),
    }),
    [portalId, variantColorResolver],
  );
  const pageTheme = useMemo(() => ({ variantColorResolver }), [variantColorResolver]);

  return (
    <div id={id} className={className} data-stage-scope={id}>
      <MantineProvider
        // oxlint-disable-next-line typescript/no-non-null-assertion
        getRootElement={() => document.getElementById(id)!}
        // One selector matching both nodes, so Mantine's light block can't match either
        cssVariablesSelector={`[data-stage-scope="${id}"]`}
        cssVariablesResolver={stageVariables}
        forceColorScheme="dark"
        theme={theme}
      >
        <PageThemeContext value={pageTheme}>{children}</PageThemeContext>
      </MantineProvider>
      {createPortal(
        <div
          id={portalId}
          data-stage-scope={id}
          data-mantine-color-scheme="dark"
          style={{ color: 'var(--mantine-color-text)' }}
        />,
        document.body,
      )}
    </div>
  );
};
